import React, { useEffect, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import {
  Plus, X, Trash2, Film, ChevronDown, ChevronRight, Check,
  Calendar, Settings2, ExternalLink, CheckCircle2, Circle,
  Folder, FolderOpen, Wand2, Clapperboard, Star, Layers, Video,
} from 'lucide-react'

/* ── Checkbox custom ─────────────────────────────────────────────────────────── */
const Checkbox = ({ checked, indeterminate = false, onChange }) => {
  const ref = useRef(null)
  useEffect(() => { if (ref.current) ref.current.indeterminate = indeterminate }, [indeterminate])
  return (
    <div
      onClick={e => { e.stopPropagation(); onChange(!checked) }}
      className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 cursor-pointer border transition-all select-none ${
        checked || indeterminate
          ? 'bg-violet-600 border-violet-500'
          : 'bg-gray-800 border-gray-600 hover:border-gray-400'
      }`}
    >
      {checked && <Check size={10} className="text-white" strokeWidth={3} />}
      {!checked && indeterminate && <span className="w-2 h-0.5 bg-white rounded-full block" />}
    </div>
  )
}

/* ── Status config ──────────────────────────────────────────────────────────── */
const STATI = {
  non_valutato: { label: 'Non valutato', row: '',                   badge: 'bg-gray-800 text-gray-500 border-gray-700' },
  buono:        { label: 'Buono',        row: 'bg-green-950/20',    badge: 'bg-green-900/50 text-green-400 border-green-700/60' },
  da_rivedere:  { label: 'Da rivedere',  row: 'bg-amber-950/20',    badge: 'bg-amber-900/50 text-amber-400 border-amber-700/60' },
  da_scartare:  { label: 'Da scartare',  row: 'bg-red-950/20',      badge: 'bg-red-900/50 text-red-400 border-red-700/60' },
}

const STATI_PROGETTO = {
  da_fare:   { label: 'Da fare',   row: '',                 badge: 'bg-gray-800 text-gray-400 border-gray-600' },
  revisione: { label: 'Revisione', row: 'bg-blue-950/20',   badge: 'bg-blue-900/50 text-blue-400 border-blue-700/60' },
  completa:  { label: 'Completa',  row: 'bg-green-950/20',  badge: 'bg-green-900/50 text-green-400 border-green-700/60' },
}

const fmtDate = d =>
  d ? new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

/* ── StatoDropdown ──────────────────────────────────────────────────────────── */
const StatoDropdown = ({ value, onChange, statiMap = STATI }) => {
  const [open, setOpen] = useState(false)
  const [pos,  setPos]  = useState({ top: 0, left: 0, width: 0, flip: false })
  const btnRef  = useRef(null)
  const menuRef = useRef(null)
  const st = statiMap[value] || Object.values(statiMap)[0]

  const openDropdown = e => {
    e.stopPropagation()
    const r = btnRef.current?.getBoundingClientRect()
    if (r) {
      const itemH = 36
      const nItems = Object.keys(statiMap).length
      const menuH = nItems * itemH + 8
      const spaceBelow = window.innerHeight - r.bottom
      const flip = spaceBelow < menuH + 8
      setPos({
        top: flip ? r.top - menuH - 4 : r.bottom + 4,
        left: r.left,
        width: Math.max(r.width, 152),
        flip,
      })
    }
    setOpen(v => !v)
  }

  useEffect(() => {
    if (!open) return
    const onClose = e => {
      if (!btnRef.current?.contains(e.target) && !menuRef.current?.contains(e.target))
        setOpen(false)
    }
    const onScroll = () => setOpen(false)
    document.addEventListener('mousedown', onClose)
    document.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onClose)
      document.removeEventListener('scroll', onScroll, true)
    }
  }, [open])

  const dropdown = open ? createPortal(
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        minWidth: pos.width,
        zIndex: 9999,
        backgroundColor: '#1a1d27',
        border: '1px solid #374151',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
      }}
    >
      {Object.entries(statiMap).map(([key, cfg]) => {
        const textCls = cfg.badge.split(' ').find(c => c.startsWith('text-')) ?? 'text-gray-300'
        const isSelected = value === key
        return (
          <button
            key={key}
            onClick={() => { onChange(key); setOpen(false) }}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-xs font-medium transition-colors hover:bg-white/5 ${textCls} ${isSelected ? 'bg-white/[0.07]' : ''}`}
          >
            <span className="w-3 flex-shrink-0 flex items-center justify-center">
              {isSelected && <Check size={10} strokeWidth={3} />}
            </span>
            {cfg.label}
          </button>
        )
      })}
    </div>,
    document.body
  ) : null

  return (
    <div className="w-full">
      <button
        ref={btnRef}
        onClick={openDropdown}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium w-full justify-between transition-colors ${st.badge}`}>
        <span className="truncate">{st.label}</span>
        <ChevronDown size={10} className="flex-shrink-0" />
      </button>
      {dropdown}
    </div>
  )
}

/* ── EditableCell ────────────────────────────────────────────────────────────── */
const EditableCell = ({ value, onChange, placeholder, mono = false, center = false, onCopyAbove }) => {
  const [editing, setEditing] = useState(false)
  const [val, setVal]         = useState(value || '')

  useEffect(() => { if (!editing) setVal(value || '') }, [value, editing])

  const commit = useCallback((v = val) => {
    setEditing(false)
    if (v !== (value || '')) onChange(v)
  }, [val, value, onChange])

  if (editing) {
    return (
      <input
        autoFocus
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); commit() }
          if (e.key === 'Escape') { setEditing(false); setVal(value || '') }
          if (e.key === 'ArrowUp' && onCopyAbove) {
            e.preventDefault()
            const above = onCopyAbove()
            if (above != null) { setVal(above); commit(above) }
          }
        }}
        className={`w-full bg-violet-950/50 border border-violet-500/60 rounded px-2 py-0.5 text-sm text-white focus:outline-none ${mono ? 'font-mono text-xs' : ''} ${center ? 'text-center' : ''}`}
      />
    )
  }

  return (
    <div
      onClick={() => setEditing(true)}
      className={`w-full cursor-text px-2 py-0.5 rounded hover:bg-gray-800/50 transition-colors min-h-[26px] text-sm select-none ${
        value ? 'text-gray-300' : 'text-gray-700'
      } ${mono ? 'font-mono text-xs' : ''} ${center ? 'text-center' : ''}`}
    >
      {value || <span className="text-gray-700">{placeholder}</span>}
    </div>
  )
}

/* ── ContextMenu (tasto destro) ──────────────────────────────────────────────── */
const ContextMenu = ({ x, y, riga, onChangeTipo, onDelete, onClose }) => {
  const ref = useRef(null)

  useEffect(() => {
    const fn = e => { if (!ref.current?.contains(e.target)) onClose() }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [onClose])

  const altroTipo = riga.tipo === 'path' ? 'scena' : 'path'
  const altraLabel = riga.tipo === 'path' ? '🎬 Converti in Scena' : '📁 Converti in Path'

  return (
    <div
      ref={ref}
      className="fixed bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 py-1 min-w-[180px]"
      style={{ top: y, left: x }}
    >
      <div className="px-3 py-1.5 text-[10px] text-gray-600 uppercase tracking-wider font-semibold border-b border-gray-800 mb-1">
        {riga.tipo === 'path' ? 'Riga Path' : 'Riga Scena'}
      </div>
      <button
        onClick={() => { onChangeTipo(riga.id, altroTipo); onClose() }}
        className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
      >
        {altraLabel}
      </button>
      <div className="my-1 border-t border-gray-800" />
      <button
        onClick={() => { onDelete(riga.id); onClose() }}
        className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
      >
        🗑 Elimina riga
      </button>
    </div>
  )
}

/* ── SequenzaModal ───────────────────────────────────────────────────────────── */
const SequenzaModal = ({ montato, righe, onRigheChange, onClose }) => {
  const [prefix,  setPrefix]  = useState('')
  const [suffix,  setSuffix]  = useState('')
  const [start,   setStart]   = useState(0)
  const [end,     setEnd]     = useState(226)
  const [padding, setPadding] = useState(5)
  const [loading, setLoading] = useState(false)

  const count = Math.max(0, end - start + 1)
  const preview = count > 0
    ? Array.from({ length: Math.min(3, count) }, (_, i) =>
        `${prefix}${String(start + i).padStart(padding, '0')}${suffix}`)
    : []

  const generate = async () => {
    if (count <= 0 || count > 5000) return
    setLoading(true)
    const nomi = Array.from({ length: count }, (_, i) =>
      `${prefix}${String(start + i).padStart(padding, '0')}${suffix}`)
    const inserts = nomi.map((nome, i) => ({
      montato_id: montato.id,
      nome,
      ordine: righe.length + i,
      status: 'non_valutato',
      tipo: 'scena',
    }))
    const { data } = await supabase.from('montati_sequenze').insert(inserts).select()
    if (data) onRigheChange([...righe, ...data])
    setLoading(false)
    onClose()
  }

  const inp = 'bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500 transition-colors w-full'

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-800">
          <Wand2 size={16} className="text-violet-400" />
          <h2 className="text-white font-bold flex-1">Genera sequenza file</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Range numerico */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Da</label>
              <input type="number" value={start} min={0} onChange={e => setStart(Number(e.target.value))} className={inp} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">A</label>
              <input type="number" value={end} min={start} onChange={e => setEnd(Number(e.target.value))} className={inp} />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Cifre</label>
              <input type="number" value={padding} min={1} max={10} onChange={e => setPadding(Number(e.target.value))} className={inp} />
            </div>
          </div>
          {/* Prefix / Suffix */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Prefisso</label>
              <input value={prefix} onChange={e => setPrefix(e.target.value)} className={inp} placeholder="A001_" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Suffisso</label>
              <input value={suffix} onChange={e => setSuffix(e.target.value)} className={inp} placeholder=".mp4" />
            </div>
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <div className="bg-gray-800/60 rounded-xl px-4 py-3">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Anteprima</p>
              <div className="space-y-1">
                {preview.map((n, i) => (
                  <p key={i} className="text-xs font-mono text-gray-300">{n}</p>
                ))}
                {count > 3 && <p className="text-xs text-gray-600">…e altri {count - 3} file</p>}
              </div>
              <p className="text-[10px] text-violet-400 mt-2 font-medium">{count} righe totali</p>
            </div>
          )}

          {count > 5000 && (
            <p className="text-xs text-red-400">Massimo 5000 righe per volta.</p>
          )}

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-gray-700 text-gray-400 hover:text-white text-sm transition-colors">
              Annulla
            </button>
            <button
              onClick={generate}
              disabled={loading || count <= 0 || count > 5000}
              className="flex-1 py-2.5 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium text-sm transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Genero...</>
              ) : (
                <><Wand2 size={14} /> Genera {count > 0 ? count : ''} righe</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── SpreadsheetTable ────────────────────────────────────────────────────────── */
const SpreadsheetTable = ({ montato, righe, onRigheChange }) => {
  const [newNome,          setNewNome]          = useState('')
  const [newTipo,          setNewTipo]          = useState('scena')
  const [ctxMenu,          setCtxMenu]          = useState(null)
  const [showSeq,          setShowSeq]          = useState(false)
  const [selected,         setSelected]         = useState(new Set())
  const [deleting,         setDeleting]         = useState(false)
  const [collapsedFolders, setCollapsedFolders] = useState(new Set()) // folder ids chiuse
  const inputRef = useRef(null)

  const toggleFolder = id => setCollapsedFolders(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const toggleSelect = id => setSelected(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const toggleAll = () => setSelected(prev =>
    prev.size === righe.length ? new Set() : new Set(righe.map(r => r.id))
  )
  const deleteSelected = useCallback(async () => {
    if (!selected.size) return
    setDeleting(true)
    const ids = [...selected]
    await supabase.from('montati_sequenze').delete().in('id', ids)
    onRigheChange(righe.filter(r => !ids.includes(r.id)))
    setSelected(new Set())
    setDeleting(false)
  }, [selected, righe, onRigheChange])

  /* Chiudi context menu su Escape */
  useEffect(() => {
    const fn = e => { if (e.key === 'Escape') setCtxMenu(null) }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
  }, [])

  const addRiga = useCallback(async (nome = '') => {
    const { data } = await supabase.from('montati_sequenze').insert({
      montato_id: montato.id,
      nome: nome.trim() || (newTipo === 'path' ? '/path/cartella/' : '—'),
      ordine: righe.length,
      status: (montato.tipo || 'girato') === 'status_progetto' ? 'da_fare' : 'non_valutato',
      tipo: newTipo,
    }).select().single()
    if (data) onRigheChange([...righe, data])
    setNewNome('')
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [montato.id, montato.tipo, righe, onRigheChange, newTipo])

  const updateRiga = useCallback(async (id, field, value) => {
    await supabase.from('montati_sequenze').update({ [field]: value }).eq('id', id)
    onRigheChange(righe.map(r => r.id === id ? { ...r, [field]: value } : r))
  }, [righe, onRigheChange])

  const deleteRiga = useCallback(async id => {
    await supabase.from('montati_sequenze').delete().eq('id', id)
    onRigheChange(righe.filter(r => r.id !== id))
    setSelected(prev => { const next = new Set(prev); next.delete(id); return next })
  }, [righe, onRigheChange])

  const handleContextMenu = (e, riga) => {
    e.preventDefault()
    setCtxMenu({ x: e.clientX, y: e.clientY, riga })
  }

  const thCls     = 'px-3 py-2.5 text-left text-[10px] uppercase tracking-widest font-semibold text-gray-600 border-b border-gray-800 bg-gray-900 select-none'
  const thOptCls  = 'px-3 py-2.5 text-left text-[10px] uppercase tracking-widest font-semibold text-indigo-700 border-b border-gray-800 bg-gray-900 select-none'
  const tdCls     = 'border-b border-gray-800/50 align-middle'

  const isProgetto   = (montato.tipo || 'girato') === 'status_progetto'
  const statiCorrente = isProgetto ? STATI_PROGETTO : STATI

  const sceneRighe  = righe.filter(r => (r.tipo || 'scena') === 'scena')
  const totBuoni    = sceneRighe.filter(r => r.status === 'buono').length
  const totScarta   = sceneRighe.filter(r => r.status === 'da_scartare').length
  const totCompleta = sceneRighe.filter(r => r.status === 'completa').length
  const totRevisione= sceneRighe.filter(r => r.status === 'revisione').length
  const totStellate = sceneRighe.filter(r => r.stellina).length

  /* ── Raggruppa righe in [ { folder|null, clips[] } ] ── */
  const groups = []
  let cur = { folder: null, clips: [] }
  for (const r of righe) {
    if ((r.tipo || 'scena') === 'path') {
      groups.push(cur)
      cur = { folder: r, clips: [] }
    } else {
      cur.clips.push(r)
    }
  }
  groups.push(cur)

  let sceneCounter = 0

  return (
    <div className="flex flex-col flex-1 overflow-hidden">

      {/* ── Toolbar ── */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-gray-800 bg-gray-900/80 flex-shrink-0">
        {/* Tipo nuova riga */}
        <span className="text-xs text-gray-600 flex-shrink-0">Tipo riga:</span>
        <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-0.5">
          <button
            onClick={() => setNewTipo('scena')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
              newTipo === 'scena' ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <Clapperboard size={11} /> Scena
          </button>
          <button
            onClick={() => setNewTipo('path')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
              newTipo === 'path' ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <Folder size={11} /> Path
          </button>
        </div>

        <div className="flex-1" />

        {/* Stats */}
        {righe.length > 0 && (
          <div className="flex items-center gap-3 text-xs">
            <span className="text-gray-600">{sceneRighe.length} {isProgetto ? 'asset' : 'clip'}</span>
            {isProgetto ? (<>
              <span className="text-green-400">{totCompleta} complet{totCompleta === 1 ? 'a' : 'e'}</span>
              <span className="text-blue-400">{totRevisione} in revisione</span>
            </>) : (<>
              <span className="text-green-400">{totBuoni} buoni</span>
              <span className="text-red-400">{totScarta} scartati</span>
            </>)}
            {totStellate > 0 && (
              <span className="flex items-center gap-1 text-green-300">
                <Star size={10} fill="currentColor" /> {totStellate}
              </span>
            )}
          </div>
        )}

        {/* Elimina selezionati */}
        {selected.size > 0 && (
          <button
            onClick={deleteSelected}
            disabled={deleting}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 border border-red-500/30 text-red-400 hover:text-red-300 rounded-lg text-xs font-medium transition-colors flex-shrink-0"
          >
            {deleting
              ? <><div className="w-3 h-3 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" /> Elimino...</>
              : <><Trash2 size={12} /> Elimina {selected.size} {selected.size === 1 ? 'riga' : 'righe'}</>
            }
          </button>
        )}

        {/* Genera sequenza */}
        <button
          onClick={() => setShowSeq(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600/20 hover:bg-violet-600/40 border border-violet-500/30 text-violet-400 hover:text-violet-300 rounded-lg text-xs font-medium transition-colors flex-shrink-0"
        >
          <Wand2 size={12} /> Genera sequenza
        </button>
      </div>

      {/* ── Table ── */}
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse min-w-[760px]">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className={`${thCls} w-9 text-center border-r border-gray-800`}>
                <div className="flex justify-center">
                  <Checkbox
                    checked={righe.length > 0 && selected.size === righe.length}
                    indeterminate={selected.size > 0 && selected.size < righe.length}
                    onChange={toggleAll}
                  />
                </div>
              </th>
              <th className={`${thCls} w-9 text-right border-r border-gray-800`}>#</th>
              <th className={`${thCls} border-r border-gray-800 w-[240px]`}>Nome File / Path</th>
              <th className={`${thCls} border-r border-gray-800 w-[140px]`}>Stato</th>
              {!isProgetto && <>
                <th className={`${thCls} border-r border-gray-800 w-[110px] text-center`}>TC IN</th>
                <th className={`${thCls} border-r border-gray-800 w-[110px] text-center`}>TC OUT</th>
              </>}
              <th className={`${thCls} border-r border-gray-800 w-[80px] text-center`}>Durata</th>
              <th className={`${thCls} border-r border-gray-800`}>Note</th>
              {isProgetto && montato.has_animatic    && <th className={`${thOptCls} border-r border-gray-800 w-[130px]`}>Animatic</th>}
              {isProgetto && montato.has_simulation  && <th className={`${thOptCls} border-r border-gray-800 w-[130px]`}>Simulation</th>}
              {isProgetto && montato.has_compositing && <th className={`${thOptCls} border-r border-gray-800 w-[130px]`}>Compositing</th>}
              {isProgetto && montato.has_environment && <th className={`${thOptCls} border-r border-gray-800 w-[130px]`}>Environment</th>}
              {isProgetto && montato.has_asset       && <th className={`${thOptCls} border-r border-gray-800 w-[130px]`}>Asset</th>}
              {isProgetto && montato.has_lighting    && <th className={`${thOptCls} border-r border-gray-800 w-[130px]`}>Lighting</th>}
              <th className={`${thCls} w-9 text-center border-r border-gray-800`}>
                <Star size={10} className="text-gray-700 mx-auto" />
              </th>
              <th className="w-9 border-b border-gray-800 bg-gray-900" />
            </tr>
          </thead>
          <tbody>
            {groups.map((group, gi) => {
              const folder   = group.folder
              const isOpen   = !folder || !collapsedFolders.has(folder.id)
              const folderSel = folder && selected.has(folder.id)

              return (
                <React.Fragment key={folder?.id ?? `root-${gi}`}>

                  {/* ── FOLDER row ── */}
                  {folder && (
                    <tr
                      className={`group transition-colors ${folderSel ? 'bg-violet-950/40' : 'bg-cyan-950/40 hover:bg-cyan-950/60'}`}
                      onContextMenu={e => handleContextMenu(e, folder)}
                    >
                      {/* checkbox */}
                      <td className={`${tdCls} border-r border-gray-800/50 px-2 text-center w-9`}>
                        <div className="flex justify-center">
                          <Checkbox checked={folderSel} onChange={() => toggleSelect(folder.id)} />
                        </div>
                      </td>
                      {/* toggle expand */}
                      <td className={`${tdCls} border-r border-gray-800/50 w-9 text-center`}>
                        <button
                          onClick={() => toggleFolder(folder.id)}
                          className="w-full h-full flex items-center justify-center text-cyan-600 hover:text-cyan-400 transition-colors py-2"
                        >
                          {isOpen
                            ? <ChevronDown size={13} />
                            : <ChevronRight size={13} />}
                        </button>
                      </td>
                      {/* path + clip count */}
                      <td colSpan={6} className={`${tdCls} px-1 border-l-2 border-l-cyan-600/50`}>
                        <div className="flex items-center gap-2">
                          {isOpen
                            ? <FolderOpen size={13} className="text-cyan-500 flex-shrink-0 ml-1" />
                            : <Folder     size={13} className="text-cyan-600 flex-shrink-0 ml-1" />}
                          <EditableCell
                            value={folder.nome}
                            onChange={v => updateRiga(folder.id, 'nome', v)}
                            placeholder="/path/cartella/"
                            mono
                          />
                          {!isOpen && group.clips.length > 0 && (
                            <span className="text-[10px] text-cyan-800 flex-shrink-0 pr-2">
                              {group.clips.length} clip nascost{group.clips.length === 1 ? 'a' : 'e'}
                            </span>
                          )}
                        </div>
                      </td>
                      {/* delete */}
                      <td className={`${tdCls} px-1.5 w-9`}>
                        <button onClick={() => deleteRiga(folder.id)}
                          className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center text-gray-700 hover:text-red-400 transition-all rounded mx-auto">
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  )}

                  {/* ── SCENA rows (nascosti se cartella chiusa) ── */}
                  {isOpen && group.clips.map((r, rIdx) => {
                    sceneCounter++
                    const st       = statiCorrente[r.status] || Object.values(statiCorrente)[0]
                    const isSel    = selected.has(r.id)
                    const isStella = !!r.stellina
                    const indented = !!folder
                    return (
                      <tr
                        key={r.id}
                        className={`group transition-colors ${
                          isSel      ? 'bg-violet-950/40' :
                          isStella   ? 'bg-green-800/30 hover:bg-green-800/40' :
                          `hover:brightness-125 ${st.row}`
                        }`}
                        onContextMenu={e => handleContextMenu(e, r)}
                      >
                        <td className={`${tdCls} border-r border-gray-800/50 px-2 text-center w-9`}>
                          <div className="flex justify-center">
                            <Checkbox checked={isSel} onChange={() => toggleSelect(r.id)} />
                          </div>
                        </td>
                        <td className={`${tdCls} border-r border-gray-800/50 px-2 text-xs text-gray-700 text-right w-9`}>{sceneCounter}</td>
                        <td className={`${tdCls} border-r border-gray-800/50 px-1 w-[240px] ${indented ? 'pl-5' : ''}`}>
                          <EditableCell value={r.nome} onChange={v => updateRiga(r.id, 'nome', v)} placeholder="nome file..." />
                        </td>
                        <td className={`${tdCls} border-r border-gray-800/50 px-1.5 py-1.5 w-[140px]`}>
                          <StatoDropdown value={r.status} onChange={v => updateRiga(r.id, 'status', v)} statiMap={statiCorrente} />
                        </td>
                        {!isProgetto && <>
                          <td className={`${tdCls} border-r border-gray-800/50 px-1 w-[110px]`}>
                            <EditableCell value={r.timecode_in} onChange={v => updateRiga(r.id, 'timecode_in', v)} placeholder="00:00:00:00" mono center />
                          </td>
                          <td className={`${tdCls} border-r border-gray-800/50 px-1 w-[110px]`}>
                            <EditableCell value={r.timecode_out} onChange={v => updateRiga(r.id, 'timecode_out', v)} placeholder="00:00:00:00" mono center />
                          </td>
                        </>}
                        <td className={`${tdCls} border-r border-gray-800/50 px-1 w-[80px]`}>
                          <EditableCell value={r.durata} onChange={v => updateRiga(r.id, 'durata', v)} placeholder="0:00" center />
                        </td>
                        <td className={`${tdCls} border-r border-gray-800/50 px-1`}>
                          <EditableCell value={r.note} onChange={v => updateRiga(r.id, 'note', v)} placeholder="note..."
                            onCopyAbove={rIdx > 0 ? () => group.clips[rIdx - 1]?.note ?? '' : undefined} />
                        </td>
                        {isProgetto && montato.has_animatic && (
                          <td className={`${tdCls} border-r border-gray-800/50 px-1.5 py-1.5 w-[130px]`}>
                            <StatoDropdown value={r.stato_animatic    || 'da_fare'} onChange={v => updateRiga(r.id, 'stato_animatic', v)}    statiMap={STATI_PROGETTO} />
                          </td>
                        )}
                        {isProgetto && montato.has_simulation && (
                          <td className={`${tdCls} border-r border-gray-800/50 px-1.5 py-1.5 w-[130px]`}>
                            <StatoDropdown value={r.stato_simulation  || 'da_fare'} onChange={v => updateRiga(r.id, 'stato_simulation', v)}  statiMap={STATI_PROGETTO} />
                          </td>
                        )}
                        {isProgetto && montato.has_compositing && (
                          <td className={`${tdCls} border-r border-gray-800/50 px-1.5 py-1.5 w-[130px]`}>
                            <StatoDropdown value={r.stato_compositing || 'da_fare'} onChange={v => updateRiga(r.id, 'stato_compositing', v)} statiMap={STATI_PROGETTO} />
                          </td>
                        )}
                        {isProgetto && montato.has_environment && (
                          <td className={`${tdCls} border-r border-gray-800/50 px-1.5 py-1.5 w-[130px]`}>
                            <StatoDropdown value={r.stato_environment || 'da_fare'} onChange={v => updateRiga(r.id, 'stato_environment', v)} statiMap={STATI_PROGETTO} />
                          </td>
                        )}
                        {isProgetto && montato.has_asset && (
                          <td className={`${tdCls} border-r border-gray-800/50 px-1.5 py-1.5 w-[130px]`}>
                            <StatoDropdown value={r.stato_asset       || 'da_fare'} onChange={v => updateRiga(r.id, 'stato_asset', v)}       statiMap={STATI_PROGETTO} />
                          </td>
                        )}
                        {isProgetto && montato.has_lighting && (
                          <td className={`${tdCls} border-r border-gray-800/50 px-1.5 py-1.5 w-[130px]`}>
                            <StatoDropdown value={r.stato_lighting    || 'da_fare'} onChange={v => updateRiga(r.id, 'stato_lighting', v)}    statiMap={STATI_PROGETTO} />
                          </td>
                        )}
                        {/* ⭐ Stellina */}
                        <td className={`${tdCls} border-r border-gray-800/50 w-9 text-center`}>
                          <button
                            onClick={() => updateRiga(r.id, 'stellina', !r.stellina)}
                            className={`w-6 h-6 flex items-center justify-center rounded mx-auto transition-all ${
                              isStella
                                ? 'text-green-400 hover:text-green-300'
                                : 'text-gray-800 opacity-0 group-hover:opacity-100 hover:text-green-500'
                            }`}
                            title={isStella ? 'Rimuovi stellina' : 'Aggiungi stellina'}
                          >
                            <Star size={13} fill={isStella ? 'currentColor' : 'none'} />
                          </button>
                        </td>
                        <td className={`${tdCls} px-1.5 w-9`}>
                          <button onClick={() => deleteRiga(r.id)}
                            className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center text-gray-700 hover:text-red-400 transition-all rounded mx-auto">
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    )
                  })}

                </React.Fragment>
              )
            })}

            {/* Nuova riga */}
            <tr className="border-b border-gray-800/50 hover:bg-gray-800/20 transition-colors">
              <td className="w-9 border-r border-gray-800/50" />
              <td className="px-2 text-xs w-9 border-r border-gray-800/50">
                {newTipo === 'path'
                  ? <Folder size={11} className="text-cyan-800 mx-auto" />
                  : <span className="text-gray-800 block text-right">{sceneCounter + 1}</span>
                }
              </td>
              <td colSpan={7} className="px-3 py-2">
                <input
                  ref={inputRef}
                  value={newNome}
                  onChange={e => setNewNome(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addRiga(newNome) }}
                  placeholder={newTipo === 'path'
                    ? '+ Aggiungi path cartella  (Invio per confermare)'
                    : '+ Aggiungi clip / scena  (Invio per confermare)'}
                  className="w-full bg-transparent text-sm text-gray-600 placeholder-gray-700 focus:outline-none focus:text-white transition-colors"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Context menu */}
      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          riga={ctxMenu.riga}
          onChangeTipo={(id, tipo) => updateRiga(id, 'tipo', tipo)}
          onDelete={deleteRiga}
          onClose={() => setCtxMenu(null)}
        />
      )}

      {/* Genera sequenza modal */}
      {showSeq && (
        <SequenzaModal
          montato={montato}
          righe={righe}
          onRigheChange={onRigheChange}
          onClose={() => setShowSeq(false)}
        />
      )}
    </div>
  )
}

/* ── InfoBar ─────────────────────────────────────────────────────────────────── */
const InfoBar = ({ montato, profiles, progetti, onUpdate, onDelete, onClose }) => {
  const [showDetail, setShowDetail] = useState(false)
  const [confirmDel, setConfirmDel] = useState(false)
  const [versioni, setVersioni]     = useState([])
  const [assets, setAssets]         = useState([])

  const save = (field, value) => {
    supabase.from('montati').update({ [field]: value }).eq('id', montato.id)
    onUpdate({ ...montato, [field]: value })
  }

  useEffect(() => {
    if (!showDetail) return
    Promise.all([
      supabase.from('montati_versioni').select('*').eq('montato_id', montato.id).order('created_at'),
      supabase.from('montati_asset').select('*').eq('montato_id', montato.id).order('ordine'),
    ]).then(([{ data: v }, { data: a }]) => {
      setVersioni(v || [])
      setAssets(a || [])
    })
  }, [showDetail, montato.id])

  const inputCls = 'bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-violet-500 transition-colors w-full'

  return (
    <>
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-800 bg-gray-900 flex-shrink-0 flex-wrap">
        <input
          defaultValue={montato.titolo}
          onBlur={e => save('titolo', e.target.value)}
          className="bg-transparent text-white font-bold text-base focus:outline-none border-b border-transparent focus:border-gray-600 pb-0.5 transition-colors min-w-[160px] flex-1"
          placeholder="Titolo bollettino..."
        />
        <span className="text-xs font-bold text-violet-400 bg-violet-500/10 border border-violet-500/20 px-2 py-1 rounded-lg flex-shrink-0">
          {montato.versione_corrente || 'V1'}
        </span>
        <select defaultValue={montato.project_id || ''} onChange={e => save('project_id', e.target.value || null)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-xs text-gray-400 focus:outline-none focus:border-violet-500 transition-colors">
          <option value="">— Progetto —</option>
          {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
        </select>
        <select defaultValue={montato.editor_id || ''} onChange={e => save('editor_id', e.target.value || null)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-xs text-gray-400 focus:outline-none focus:border-violet-500 transition-colors">
          <option value="">— Editor —</option>
          {profiles.map(p => <option key={p.id} value={p.id}>{p.nickname || p.full_name}</option>)}
        </select>
        {montato.scadenza && (
          <span className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0">
            <Calendar size={11} /> {fmtDate(montato.scadenza)}
          </span>
        )}
        <button onClick={() => setShowDetail(v => !v)}
          className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${showDetail ? 'text-violet-400 bg-violet-500/10' : 'text-gray-600 hover:text-white hover:bg-gray-800'}`}>
          <Settings2 size={15} />
        </button>
        {onClose && (
          <button onClick={onClose} className="p-1.5 text-gray-600 hover:text-white hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0">
            <X size={15} />
          </button>
        )}
      </div>

      {showDetail && (
        <div className="border-b border-gray-800 bg-gray-900/60 px-4 py-4 flex-shrink-0 overflow-y-auto max-h-64">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-[10px] text-gray-600 uppercase tracking-wider mb-1 block">Data inizio</label>
              <input type="date" defaultValue={montato.data_inizio || ''} onBlur={e => save('data_inizio', e.target.value || null)} className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] text-gray-600 uppercase tracking-wider mb-1 block">Scadenza</label>
              <input type="date" defaultValue={montato.scadenza || ''} onBlur={e => save('scadenza', e.target.value || null)} className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] text-gray-600 uppercase tracking-wider mb-1 block">Formato consegna</label>
              <input defaultValue={montato.formato_consegna || ''} onBlur={e => save('formato_consegna', e.target.value)} placeholder="MP4, MOV..." className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] text-gray-600 uppercase tracking-wider mb-1 block">Canale</label>
              <input defaultValue={montato.canale_consegna || ''} onBlur={e => save('canale_consegna', e.target.value)} placeholder="YouTube, Insta..." className={inputCls} />
            </div>
          </div>
          <div className="mt-3">
            <label className="text-[10px] text-gray-600 uppercase tracking-wider mb-1 block">Note di regia / Brief</label>
            <textarea defaultValue={montato.note_regia || ''} onBlur={e => save('note_regia', e.target.value)} rows={2}
              placeholder="Tono, stile, istruzioni..." className={`${inputCls} resize-none`} />
          </div>

          {/* Colonne opzionali — solo per status_progetto */}
          {(montato.tipo || 'girato') === 'status_progetto' && (
            <div className="mt-3 pt-3 border-t border-gray-800/60">
              <label className="text-[10px] text-gray-600 uppercase tracking-wider mb-2 block">Colonne extra</label>
              <div className="flex gap-3 flex-wrap">
                {[
                  { field: 'has_animatic',    label: 'Animatic' },
                  { field: 'has_simulation',  label: 'Simulation' },
                  { field: 'has_compositing', label: 'Compositing' },
                  { field: 'has_environment', label: 'Environment' },
                  { field: 'has_asset',       label: 'Asset' },
                  { field: 'has_lighting',    label: 'Lighting' },
                ].map(({ field, label }) => (
                  <button
                    key={field}
                    onClick={() => save(field, !montato[field])}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      montato[field]
                        ? 'bg-indigo-600/20 text-indigo-300 border-indigo-500/40 hover:bg-indigo-600/30'
                        : 'bg-gray-800 text-gray-500 border-gray-700 hover:border-gray-500'
                    }`}
                  >
                    <span className={`w-3 h-3 rounded border flex items-center justify-center ${montato[field] ? 'bg-indigo-500 border-indigo-400' : 'border-gray-600'}`}>
                      {montato[field] && <Check size={8} className="text-white" strokeWidth={3} />}
                    </span>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {versioni.length > 0 && (
            <div className="mt-3">
              <label className="text-[10px] text-gray-600 uppercase tracking-wider mb-2 block">Versioni</label>
              <div className="flex gap-2 flex-wrap">
                {versioni.map(v => (
                  <span key={v.id} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border ${v.approvata ? 'text-green-400 border-green-700/50 bg-green-900/30' : 'text-violet-400 border-violet-700/50 bg-violet-900/30'}`}>
                    {v.numero}
                    {v.link && <a href={v.link} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}><ExternalLink size={9} /></a>}
                  </span>
                ))}
              </div>
            </div>
          )}
          {assets.length > 0 && (
            <div className="mt-3">
              <label className="text-[10px] text-gray-600 uppercase tracking-wider mb-2 block">Asset ({assets.filter(a => a.ricevuto).length}/{assets.length})</label>
              <div className="flex gap-2 flex-wrap">
                {assets.map(a => (
                  <button key={a.id} onClick={() => {
                    supabase.from('montati_asset').update({ ricevuto: !a.ricevuto }).eq('id', a.id)
                    setAssets(p => p.map(x => x.id === a.id ? { ...x, ricevuto: !x.ricevuto } : x))
                  }} className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg border transition-colors ${a.ricevuto ? 'text-green-400 border-green-700/50 bg-green-900/30 line-through' : 'text-gray-500 border-gray-700 hover:border-gray-600'}`}>
                    {a.ricevuto ? <CheckCircle2 size={10} /> : <Circle size={10} />} {a.nome}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="mt-4 pt-3 border-t border-gray-800/60">
            <button onClick={() => {
              if (!confirmDel) { setConfirmDel(true); return }
              supabase.from('montati').delete().eq('id', montato.id).then(() => onDelete(montato.id))
            }} className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${confirmDel ? 'bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white' : 'text-gray-700 hover:text-red-400 hover:bg-gray-800'}`}>
              {confirmDel ? 'Conferma eliminazione' : 'Elimina bollettino'}
            </button>
          </div>
        </div>
      )}
    </>
  )
}

/* ── Sidebar lista bollettini ────────────────────────────────────────────────── */
const BollettiniSidebar = ({ montati, selectedId, onSelect, onNew }) => (
  <div className="w-56 flex-shrink-0 border-r border-gray-800 flex flex-col bg-gray-900/40 overflow-hidden">
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Bollettini</span>
      <button onClick={onNew} className="text-gray-600 hover:text-violet-400 transition-colors" title="Nuovo"><Plus size={15} /></button>
    </div>
    <div className="flex-1 overflow-y-auto py-1">
      {montati.length === 0 && (
        <p className="text-gray-700 text-xs text-center py-6 px-4">Nessun bollettino.<br />Creane uno con +</p>
      )}
      {montati.map(m => (
        <button key={m.id} onClick={() => onSelect(m)}
          className={`w-full text-left px-4 py-2.5 transition-colors border-l-2 ${
            selectedId === m.id
              ? 'border-violet-500 bg-violet-500/10 text-white'
              : 'border-transparent text-gray-400 hover:text-white hover:bg-gray-800/50'
          }`}>
          <p className="text-sm font-medium truncate">{m.titolo}</p>
          <p className="text-[10px] text-gray-600 mt-0.5">{m.versione_corrente || 'V1'}{m.progetto ? ` · ${m.progetto.titolo}` : ''}</p>
        </button>
      ))}
    </div>
  </div>
)

/* ── NewBollettino Modal ─────────────────────────────────────────────────────── */
const NewModal = ({ progetti, profiles, userId, onClose, onCreated }) => {
  const [titolo,    setTitolo]    = useState('')
  const [projectId, setProject]   = useState('')
  const [editorId,  setEditor]    = useState(userId || '')
  const [tipo,      setTipo]      = useState('girato')
  const [hasAnim,   setHasAnim]   = useState(false)
  const [hasSim,    setHasSim]    = useState(false)
  const [hasComp,   setHasComp]   = useState(false)
  const [hasEnv,    setHasEnv]    = useState(false)
  const [hasAsset,  setHasAsset]  = useState(false)
  const [hasLight,  setHasLight]  = useState(false)
  const [loading,   setLoading]   = useState(false)

  const create = async e => {
    e.preventDefault()
    if (!titolo.trim()) return
    setLoading(true)
    const { data } = await supabase.from('montati').insert({
      titolo: titolo.trim(),
      project_id: projectId || null,
      editor_id: editorId || null,
      versione_corrente: 'V1',
      created_by: userId,
      tipo,
      has_animatic:    tipo === 'status_progetto' ? hasAnim  : false,
      has_simulation:  tipo === 'status_progetto' ? hasSim   : false,
      has_compositing: tipo === 'status_progetto' ? hasComp  : false,
      has_environment: tipo === 'status_progetto' ? hasEnv   : false,
      has_asset:       tipo === 'status_progetto' ? hasAsset : false,
      has_lighting:    tipo === 'status_progetto' ? hasLight : false,
    }).select('*, progetto:project_id(titolo,colore), editor:editor_id(nickname,full_name)').single()
    setLoading(false)
    if (data) onCreated(data)
    onClose()
  }

  const inp = 'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-violet-500 transition-colors'

  const tipoBtn = (t, icon, label, desc) => (
    <button
      type="button"
      onClick={() => setTipo(t)}
      className={`flex-1 flex flex-col items-center gap-2 px-3 py-4 rounded-xl border text-center transition-all ${
        tipo === t
          ? 'bg-violet-600/20 border-violet-500/60 text-white'
          : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
      }`}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${tipo === t ? 'bg-violet-600/30' : 'bg-gray-700'}`}>
        {icon}
      </div>
      <div>
        <p className="text-xs font-semibold">{label}</p>
        <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">{desc}</p>
      </div>
    </button>
  )

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <h2 className="text-white font-bold">Nuovo bollettino</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={18} /></button>
        </div>
        <form onSubmit={create} className="p-5 space-y-3">

          {/* Tipo bollettino */}
          <div>
            <label className="text-xs text-gray-500 mb-2 block">Tipo</label>
            <div className="flex gap-2">
              {tipoBtn('girato',          <Video size={16} />,  'Girato',         'TC IN/OUT · Buono · Scarta')}
              {tipoBtn('status_progetto', <Layers size={16} />, 'Status Progetto','Da fare · Completa · Revisione')}
            </div>
          </div>

          {/* Colonne opzionali — solo status_progetto */}
          {tipo === 'status_progetto' && (
            <div>
              <label className="text-xs text-gray-500 mb-2 block">Colonne extra (opzionali)</label>
              <div className="flex gap-2 flex-wrap">
                {[
                  { state: hasAnim,  set: setHasAnim,  label: 'Animatic' },
                  { state: hasSim,   set: setHasSim,   label: 'Simulation' },
                  { state: hasComp,  set: setHasComp,  label: 'Compositing' },
                  { state: hasEnv,   set: setHasEnv,   label: 'Environment' },
                  { state: hasAsset, set: setHasAsset, label: 'Asset' },
                  { state: hasLight, set: setHasLight, label: 'Lighting' },
                ].map(({ state, set, label }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => set(v => !v)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      state
                        ? 'bg-indigo-600/20 text-indigo-300 border-indigo-500/40'
                        : 'bg-gray-800 text-gray-500 border-gray-700 hover:border-gray-500'
                    }`}
                  >
                    <span className={`w-3 h-3 rounded border flex items-center justify-center ${state ? 'bg-indigo-500 border-indigo-400' : 'border-gray-600'}`}>
                      {state && <Check size={8} className="text-white" strokeWidth={3} />}
                    </span>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Titolo *</label>
            <input autoFocus value={titolo} onChange={e => setTitolo(e.target.value)} required className={inp} placeholder="es. Spot Nike — Giugno" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Progetto</label>
              <select value={projectId} onChange={e => setProject(e.target.value)} className={inp}>
                <option value="">— Nessuno —</option>
                {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Editor</label>
              <select value={editorId} onChange={e => setEditor(e.target.value)} className={inp}>
                <option value="">— Nessuno —</option>
                {profiles.map(p => <option key={p.id} value={p.id}>{p.nickname || p.full_name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border border-gray-700 text-gray-400 hover:text-white text-sm transition-colors">Annulla</button>
            <button type="submit" disabled={loading || !titolo.trim()}
              className="flex-1 py-2.5 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium text-sm transition-colors">
              {loading ? 'Creo...' : 'Crea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/* ── Main ────────────────────────────────────────────────────────────────────── */
const Montati = () => {
  const { profile } = useAuth()
  const [montati, setMontati]         = useState([])
  const [profiles, setProfiles]       = useState([])
  const [progetti, setProgetti]       = useState([])
  const [righe, setRighe]             = useState([])
  const [selectedId, setSelectedId]   = useState(null)
  const [loading, setLoading]         = useState(true)
  const [showNew, setShowNew]         = useState(false)
  const [loadingRighe, setLoadingRighe] = useState(false)

  // Sempre derivato da montati → mai stale
  const selected = montati.find(m => m.id === selectedId) || null

  const fetchMontati = useCallback(async () => {
    const { data } = await supabase
      .from('montati')
      .select('*, progetto:project_id(titolo,colore), editor:editor_id(nickname,full_name)')
      .order('created_at', { ascending: false })
    setMontati(data || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchMontati()
    Promise.all([
      supabase.from('profiles').select('id,nickname,full_name').order('nickname'),
      supabase.from('progetti').select('id,titolo,colore').neq('status','archiviato').order('titolo'),
    ]).then(([{ data: pf }, { data: pr }]) => {
      setProfiles(pf || [])
      setProgetti(pr || [])
    })
  }, [fetchMontati])

  useEffect(() => {
    if (!selectedId) { setRighe([]); return }
    setLoadingRighe(true)
    supabase.from('montati_sequenze').select('*')
      .eq('montato_id', selectedId).order('ordine')
      .then(({ data }) => { setRighe(data || []); setLoadingRighe(false) })
  }, [selectedId])

  const handleCreated = m => { setMontati(p => [m, ...p]); setSelectedId(m.id) }
  const handleUpdate  = m => { setMontati(p => p.map(x => x.id === m.id ? { ...x, ...m } : x)) }
  const handleDelete  = id => { setMontati(p => p.filter(m => m.id !== id)); if (selectedId === id) setSelectedId(null) }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  return (
    <div className="flex h-[calc(100vh-4rem)] -mt-8 -ml-8 -mr-8 overflow-hidden">
      <BollettiniSidebar
        montati={montati}
        selectedId={selectedId}
        onSelect={m => setSelectedId(m.id)}
        onNew={() => setShowNew(true)}
      />
      {/* key sul contenitore → React smonta tutto quando cambia la selezione */}
      <div key={selectedId ?? '__empty__'} className="flex-1 flex flex-col overflow-hidden bg-gray-950">
        {!selected ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <Film size={48} className="text-gray-800 mb-4" />
            <p className="text-gray-600 text-sm mb-2">Seleziona un bollettino dalla sidebar</p>
            <button onClick={() => setShowNew(true)}
              className="flex items-center gap-2 text-violet-400 hover:text-violet-300 text-sm transition-colors mt-2">
              <Plus size={14} /> Crea il primo bollettino
            </button>
          </div>
        ) : (
          <>
            <InfoBar
              montato={selected}
              profiles={profiles}
              progetti={progetti}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onClose={() => setSelectedId(null)}
            />
            {loadingRighe ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <SpreadsheetTable
                montato={selected}
                righe={righe}
                onRigheChange={setRighe}
              />
            )}
          </>
        )}
      </div>
      {showNew && (
        <NewModal
          progetti={progetti}
          profiles={profiles}
          userId={profile?.id}
          onClose={() => setShowNew(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  )
}

export default Montati
